import axios from 'axios';
import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

export async function getMotivationalMessage(horoscopes, members) {
  // Compose a professional, insightful team review based on zodiac composition
  const memberList = members.map(m => `${m.name} (${m.birthdate})`).join(', ');
  const prompt = `გუნდში არიან შემდეგი წევრები: ${memberList}\nგუნდს აქვს შემდეგი ასტროლოგიური პროგნოზები:\n${horoscopes.map(h => `- ${h}`).join('\n')}\n\nშეაფასე გუნდის ასტროლოგიური შემადგენლობა პროფესიონალურად: რომელი ზოდიაქოს ნიშნები არიან გუნდში, როგორია მათი ურთიერთქმედების პოტენციალი, არის თუ არა ჰარმონია ან დაძაბულობა, რა შეიძლება იყოს გუნდის ძლიერი და სუსტი მხარეები ასტროლოგიური თვალსაზრისით. დაწერე მოკლე, პროფესიონალური და დამაჯერებელი შეფასება ქართულად, როგორც გუნდის ასტროლოგი.
  გუნდს მიმართავ როგორც ინტერნეტ ბანკის დრაკონებს და ლეიბორისტებს. საბოლოოდ დაწერე შეჯამება თუ 10 ბალიდან რამდენად ჰარმონიულ კვირას უნდა ველოდოთ. ეცადე რომ ყველა წევრი ცალ-ცალკე არ განიხილო. არ უნდა იყოს პასუხი დიდი და დამღლელი`;
  console.log(prompt);
  const response = await axios.post(OPENAI_API_URL, {
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: 'შენ ხარ  გუნდის ასტროლოგი, წერ ქართულად.' },
      { role: 'user', content: prompt }
    ],
    max_tokens: 500,
    temperature: 0.8,
  }, {
    headers: {
      'Authorization': `Bearer ${OPENAI_API_KEY}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data.choices[0].message.content;
}

// getUserAstroDualReview generates only the serious (Georgian) review for a user. It does NOT generate daily fun lines or a funny line.
export async function getUserAstroDualReview({ name, birthdate }) {
  const prompt = `მომხმარებელი: ${name}\nდაბადების თარიღი: ${birthdate}\nდაწერე  მოკლე, სერიოზული და ამ კვირაზე ორიენტირებული ასტროლოგიური შეფასება ამ ადამიანის შესახებ ქართულად ( იუმორის და შესავლის გარეშე).`;

  const response = await axios.post(OPENAI_API_URL, {
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: 'შენ ხარ გუნდის ასტროლოგი. პასუხობ მხოლოდ ქართულად.' },
      { role: 'user', content: prompt }
    ],
    max_tokens: 120,
    temperature: 0.8,
  }, {
    headers: {
      'Authorization': `Bearer ${OPENAI_API_KEY}`,
      'Content-Type': 'application/json',
    },
  });

  // Parse the response into serious only
  const text = response.data.choices[0].message.content;
  const serious = text.trim();
  return { serious }; // Only serious, no funny or fun lines
}

export async function getDailySignFunLines() {
  const today = new Date().toISOString().slice(0, 10);
  const ref = doc(db, 'daily_fun_lines', today);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    return snap.data().lines;
  }

  const prompt = `
For each zodiac sign, write 4 short, punchy, slogan-like, and funny one-sentence lines for today:
- Developer: Make it about a common developer problem (e.g., bugs, merge conflicts, Stack Overflow, code reviews, deployments, debugging, etc.), and make it energetic and witty, like a team slogan or inside joke.
- QA: Make it about a typical QA/testing situation, also in a punchy, slogan-like style.
- Management: Make it about management/meetings/planning, with a witty, energetic twist.
- Designer: Make it about design or UI/UX, also as a slogan or energetic quip.

Format as:
Sign: Developer: ... QA: ... Management: ... Designer: ...

Examples:
Aries: Developer: Charging ahead! May break prod, but with style. QA: Found a bug, named it after the developer. Management: Scheduled a meeting to discuss meetings. Designer: Spent 2 hours picking the perfect shade of blue.
Taurus: Developer: Refactored the code until it looked exactly the same. QA: Logged a bug, then fixed it themselves. Management: Promised no more scope changes (for the third time). Designer: Pixel-pushed until the logo was off by 1px.

Write for all 12 signs. Only output the lines, no extra text.`;

  const response = await axios.post(OPENAI_API_URL, {
    model: 'gpt-4o',
    messages: [
      { role: 'system', content: 'You are a witty astrologer. Answer only in English.' },
      { role: 'user', content: prompt }
    ],
    max_tokens: 1600,
    temperature: 0.9,
  }, {
    headers: {
      'Authorization': `Bearer ${OPENAI_API_KEY}`,
      'Content-Type': 'application/json',
    },
  });

  // Parse the response into an object
  const text = response.data.choices[0].message.content;
  const lines = text.split(/\n|\r/).filter(Boolean);
  const result = {};
  let currentSign = '';
  for (const line of lines) {
    const signMatch = line.match(/^(Aries|Taurus|Gemini|Cancer|Leo|Virgo|Libra|Scorpio|Sagittarius|Capricorn|Aquarius|Pisces)\s*:/i);
    if (signMatch) {
      currentSign = signMatch[1];
      result[currentSign] = {};
      const dev = line.match(/Developer:\s*([^QAManagementDesigner]*)/i);
      const qa = line.match(/QA:\s*([^ManagementDesigner]*)/i);
      const mgmt = line.match(/Management:\s*([^Designer]*)/i);
      const designer = line.match(/Designer:\s*(.*)$/i);
      if (dev) result[currentSign].developer = dev[1].replace(/^Developer:\s*/i, '').trim();
      if (qa) result[currentSign].qa = qa[1].replace(/^QA:\s*/i, '').trim();
      if (mgmt) result[currentSign].management = mgmt[1].replace(/^Management:\s*/i, '').trim();
      if (designer) result[currentSign].designer = designer[1].replace(/^Designer:\s*/i, '').trim();
    } else if (currentSign) {
      // Sometimes lines are split, try to append
      if (!result[currentSign].developer) {
        result[currentSign].developer = line.replace(/^Developer:\s*/i, '').trim();
      } else if (!result[currentSign].qa) {
        result[currentSign].qa = line.replace(/^QA:\s*/i, '').trim();
      } else if (!result[currentSign].management) {
        result[currentSign].management = line.replace(/^Management:\s*/i, '').trim();
      } else if (!result[currentSign].designer) {
        result[currentSign].designer = line.replace(/^Designer:\s*/i, '').trim();
      }
    }
  }
  // Save to Firestore
  await setDoc(ref, { date: today, lines: result });
  return result;
} 